import { useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import { useParams } from 'react-router-dom'
import { SELECTED_FILTERS_NO_DATA_MESSAGE } from 'constants/filters'
import { toast } from 'react-toastify'
import {
  BarLegendState,
  HorizontalBarChart,
  HorizontalBarChartProps,
  InputField,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  TIMP_CHART_COLORS,
  useHorizontalBarChartPngExport,
  usePngExport
} from '@jana-technology/platform-ui'

import { SortChart } from '$/types'
import { useAppSelector } from 'app/hooks'
import { useBaselineInfoQuery } from 'app/services/api/baselines'
import { useGetQRAAssessmentQuery } from 'app/services/api/qra/assessments'
import {
  IQRAGraphCsvExportParams,
  useExportQRAConsequenceGraphMutation
} from 'app/services/api/qra/graphs'
import { useExportJobContext } from 'common/contexts/useExportJobContext'
import {
  formatWithCommasOrExponential,
  getChartLabelBasedOnRiskUnit,
  makeRUoptions
} from 'common/utils/chart/chartUtils'

import {
  IChartData,
  IQRAConsequenceCategoryTooltipGroup
} from 'features/RiskManagement/Components/Graphs'
import GraphContainerCard from 'features/RiskManagement/Components/Graphs/Components/GraphContainerCard'
import { selectRiskUnit } from 'features/RiskManagement/Components/LocalFilter/localFilterSelectors'

import {
  ChartRow,
  getChartCsvExportQueryParams,
  sortChartRows
} from './Chart.helpers'

const EXPORT_UNAVAILABLE_MESSAGE =
  'Chart image export is unavailable right now.'

type ConsequenceChartDatum = {
  category: string
  tooltipGroup?: IQRAConsequenceCategoryTooltipGroup
  [key: string]:
    | number
    | string
    | IQRAConsequenceCategoryTooltipGroup
    | undefined
}

type ConsequenceChartSeries = {
  key: string
  values: number[]
}

interface IConsequencesChart {
  showLoading: boolean
  consequenceData: IChartData
  csvExportParams?: IQRAGraphCsvExportParams
}

const CHART_TITLE = 'Risk by Consequence'
const CHART_COLORS = [...TIMP_CHART_COLORS]

const ExportableHorizontalBarChart = <D extends object>(
  props: HorizontalBarChartProps<D>
) => {
  const parentRef = useHorizontalBarChartPngExport(props)

  return (
    <div
      ref={parentRef}
      className='h-full w-full min-w-0 [&_.visx-legend-item]:pointer-events-none [&_.visx-legend-item]:!cursor-default'
    >
      <HorizontalBarChart<D> {...props} />
    </div>
  )
}

/**
 * REVIEW: Check whether we can keep this as a dumb component going forward
 *         May not be possible due to potential unique interaction logic..
 *
 * @returns interactive horizontal bar chart
 */
const ConsequencesChart = ({
  showLoading,
  consequenceData,
  csvExportParams
}: IConsequencesChart) => {
  const riskUnit = useAppSelector(selectRiskUnit)
  const [sortBy, setSortBy] = useState<SortChart>('default')
  const shouldShowConsequenceGroupNoData = false

  const { downloadImage, isReady: isChartPngExportReady } = usePngExport()

  const { assessmentId } = useParams()
  const { currentData: currentAssessment } = useGetQRAAssessmentQuery(
    assessmentId ?? skipToken
  )

  const activeThreatData = consequenceData

  const stateNames = Object.keys(activeThreatData.values)
  const primaryStateName = stateNames[0]
  const secondaryStateName = stateNames[1]
  const chartSeries: ConsequenceChartSeries[] = []

  if (primaryStateName != null) {
    chartSeries.push({
      key: activeThreatData.labels[primaryStateName] || 'Series 1',
      values: activeThreatData.values[primaryStateName] ?? []
    })
  }

  if (secondaryStateName != null) {
    chartSeries.push({
      key: activeThreatData.labels[secondaryStateName] || 'Series 2',
      values: activeThreatData.values[secondaryStateName] ?? []
    })
  }

  const hasComparisonSeries = chartSeries.length > 1
  const legendState: BarLegendState = {
    activeColors: CHART_COLORS.slice(0, chartSeries.length),
    activeDataKeys: chartSeries.map((series) => series.key),
    inactiveItems: [],
    legendColors: CHART_COLORS,
    toggleItem: () => undefined,
    highlightItem: () => undefined,
    clearHighlightedItem: () => undefined
  }

  const chartRows = activeThreatData.categories.map<ChartRow>(
    (category, index) => {
      const row: ChartRow = {
        category,
        originalIndex: index,
        primaryValue: chartSeries[0]?.values[index],
        tooltipGroup: activeThreatData.threatCategoryTooltipGroups?.[index]
      }

      for (const series of chartSeries) {
        row[series.key] = series.values[index]
      }

      return row
    }
  )

  const getFileNameForExport = () => {
    let fileName = 'Risk_By_Consequence'

    if (currentAssessment?.name) {
      fileName += `_${currentAssessment.name}`
    }

    return fileName
  }

  const handleExportChartAsPng = async () => {
    const fileName = getFileNameForExport()

    if (!isChartPngExportReady) {
      toast.error(EXPORT_UNAVAILABLE_MESSAGE)

      return
    }

    try {
      await downloadImage({ fileName, options: { title: CHART_TITLE } })
    } catch {
      toast.error(EXPORT_UNAVAILABLE_MESSAGE)
    }
  }

  const { currentData: baseline } = useBaselineInfoQuery('current')

  const [triggerExportRiskByConsequenceGraph] =
    useExportQRAConsequenceGraphMutation()

  const { createDataExportJob } = useExportJobContext()

  const handleExportChartAsCsv = async () => {
    const fileName = getFileNameForExport()
    const baselineId = baseline?.id

    if (csvExportParams) {
      createDataExportJob(
        triggerExportRiskByConsequenceGraph,
        getChartCsvExportQueryParams({
          sortBy,
          csvExportParams,
          assessmentId,
          baselineId
        }),
        fileName,
        '.csv'
      )
    }
  }

  const chartData = sortChartRows(chartRows, sortBy).map(
    (row): ConsequenceChartDatum => {
      const datum: ConsequenceChartDatum = {
        category: row.category,
        ...(row.tooltipGroup == null ? {} : { tooltipGroup: row.tooltipGroup })
      }

      for (const series of chartSeries) {
        datum[series.key] = row[series.key]
      }

      return datum
    }
  )

  return (
    <GraphContainerCard
      title={CHART_TITLE}
      optionsComponent={<CardActions sortBy={sortBy} setSortBy={setSortBy} />}
      actionMenuItems={[
        {
          children: 'Export chart as image',
          onSelect: () => {
            void handleExportChartAsPng()
          }
        },
        {
          children: 'Export chart as data',
          onSelect: () => handleExportChartAsCsv()
        }
      ]}
    >
      {shouldShowConsequenceGroupNoData || showLoading ? (
        showLoadingOrConsequenceGroupNoData(
          showLoading,
          shouldShowConsequenceGroupNoData
        )
      ) : (
        <ExportableHorizontalBarChart
          height={hasComparisonSeries ? 350 : 390}
          barWidth={hasComparisonSeries ? 6 : 16}
          groupInnerGap={4}
          chartName={CHART_TITLE}
          dataKeys={chartSeries.map((series) => series.key)}
          data={chartData}
          xAccessor={(datum) => datum.category}
          xAxisLabel={getChartLabelBasedOnRiskUnit(riskUnit)}
          xTicks={3}
          xTickFormatter={(val: number) => {
            return formatThreatValue(val)
          }}
          withScroll={false}
          legendAnchor='container-start'
          withLegend={hasComparisonSeries}
          legendState={legendState}
          colors={CHART_COLORS}
        />
      )}
    </GraphContainerCard>
  )
}

const CardActions = ({
  sortBy,
  setSortBy
}: {
  sortBy: SortChart
  setSortBy: (value: SortChart) => void
}) => (
  <InputField direction='row' className='p-4 text-sm'>
    <Label htmlFor='consequence-chart-sort-select'>Sort:</Label>
    <Select value={sortBy} onValueChange={setSortBy}>
      <SelectTrigger id='consequence-chart-sort-select' className='h-8 w-36'>
        <SelectValue />
      </SelectTrigger>
      <SelectContent className='text-sm'>
        <SelectItem value='default'>Default</SelectItem>
        <SelectItem value='risk-asc'>Risk ↑</SelectItem>
        <SelectItem value='risk-desc'>Risk ↓</SelectItem>
        <SelectItem value='alphabetical-asc'>Alphabetical ↑</SelectItem>
        <SelectItem value='alphabetical-desc'>Alphabetical ↓</SelectItem>
      </SelectContent>
    </Select>
  </InputField>
)

const formatterOptions = makeRUoptions('ru')

const formatThreatValue = (value: number) =>
  formatWithCommasOrExponential(value, formatterOptions)

const showLoadingOrConsequenceGroupNoData = (
  showLoading: boolean,
  shouldShowConsequenceGroupNoData: boolean
) => {
  if (showLoading) {
    return (
      <div className='flex h-full items-center justify-center text-center'>
        <Spinner label='Loading...' size='medium' />
      </div>
    )
  }

  if (shouldShowConsequenceGroupNoData) {
    return (
      <div className='flex h-full items-center justify-center text-center'>
        {SELECTED_FILTERS_NO_DATA_MESSAGE}
      </div>
    )
  }

  return null
}

export default ConsequencesChart
